import {AfterViewInit, Component, computed, input, output, ViewChild} from '@angular/core';
import {FormsModule, ReactiveFormsModule} from '@angular/forms';
import {TranslatePipe} from '@ngx-translate/core';
import {AppControlErrorComponent} from '@shared/components/forms/app-control-error/app-control-error.component';
import {BaseAppControlValueAccessorComponent} from '@shared/components/forms/app-control-value-accessor/base-app-control-value-accessor.component';
import {FlatpickrDefaults, FlatpickrDirective, provideFlatpickrDefaults} from 'angularx-flatpickr';
import locales from 'flatpickr/dist/l10n/pt.js';
import {FlatpickrPlaceholderSyncDirective} from '@shared/components/forms/app-datepicker/directives/datepicker.directive';
import {NgClass} from '@angular/common';
import monthSelectPlugin from "flatpickr/dist/plugins/monthSelect";

export type IconPosition = 'left' | 'right';

@Component({
    selector: 'app-datepicker',
    templateUrl: './app-date-picker.component.html',
    styleUrls: ['./app-date-picker.component.scss'],
    standalone: true,
    imports: [
        ReactiveFormsModule,
        FormsModule,
        AppControlErrorComponent,
        FlatpickrDirective,
        TranslatePipe,
        FlatpickrPlaceholderSyncDirective,
        NgClass
    ],
    providers: [provideFlatpickrDefaults()]
})
export class AppDatePickerComponent extends BaseAppControlValueAccessorComponent implements AfterViewInit {
    @ViewChild('datepicker') datepicker: any;

    // Inputs específicos do componente
    containerStyles = input<string>('');
    label = input<string>('');
    placeholder = input<string>('');
    hint = input<string>('');
    customErrors = input<Record<string, string>>({});
    showErrorDescription = input<boolean>(true);
    minDate = input<string | Date>('');
    maxDate = input<string | Date>('');
    showTime = input<boolean>(false);
    allowInput = input<boolean>(true);
    inline = input<boolean>(false);
    groupIcon = input<string>('ti-calendar');
    groupIconPosition = input<IconPosition>('left');
    mode = input<'single' | 'range'>('single');
    monthYearOnly = input<boolean>(false);


    // Outputs
    onDateChange = output<any>();

    // Estado interno do componente
    inputValue: any = '';

    // Computed properties
    hasIcon = computed(() => !!this.groupIcon());
    isLeftIcon = computed(() => this.hasIcon() && this.groupIconPosition() === 'left');
    isRightIcon = computed(() => this.hasIcon() && this.groupIconPosition() === 'right');

    inputClasses = computed(() => {
        const classes = ['form-control'];

        if (this.flatpickrOptions().inline) classes.push('d-none');
        if (this.isLeftIcon()) classes.push('input-icon-left');
        if (this.isRightIcon()) classes.push('input-icon-right');

        return classes.join(' ');
    });

    containerClasses = computed(() => {
        const classes = ['form-group'];
        if (this.containerStyles()) classes.push(this.containerStyles());
        return classes.join(' ');
    });
    flatpickrOptions: any = computed<Partial<FlatpickrDefaults>>(() => {
        const baseOptions: Partial<FlatpickrDefaults> = {
            mode: this.mode(),
            dateFormat: this.showTime() ? `Y-m-d\\TH:i:S${this.getTimezoneOffset()}` : `Y-m-d`,
            altInput: true,
            altFormat: this.showTime() ? 'd/m/Y H:i:S' : 'd/m/Y',
            enableTime: this.showTime(),
            time24hr: true,
            allowInput: this.allowInput(),
            locale: locales.pt,
            inline: this.inline(),
            disableMobile: true
        };

        const plugins: any[] = [];

        plugins.push(this.calendarButtonsPlugin());

        // Configuração para seleção de mês/ano
        if (this.monthYearOnly()) {
            plugins.push(monthSelectPlugin({
                shorthand: false,
                dateFormat: 'm/Y',
                altFormat: 'F Y'
            }));
            baseOptions.dateFormat = 'm/Y';
            baseOptions.altFormat = 'F Y';
        }

        baseOptions.plugins = plugins;

        return baseOptions;
    });

    constructor() {
        super();
    }

    ngAfterViewInit(): void {
        this.setupControlListener();
        this.datepicker.instance.config.onClose.push((dates: string[]) => {
            if (dates?.length === 0 && this.inputValue) {
                this.datepicker.instance.clear();
                this.inputValue = '';
                this.setValue('');
                this.onDateChange.emit('');
            }
        });
    }

    override writeValue(obj: any): void {
        this.inputValue = obj;
    }

    override registerOnChange(fn: any): void {
        this.onChange = fn;
    }

    override registerOnTouched(fn: any): void {
        this.onTouched = fn;
    }

    override setDisabledState(isDisabled: boolean): void {
        super.setDisabledState(isDisabled);
    }

    // Event handlers específicos do datepicker
    onModelChange(value: any): void {
        // Se for modo range, só emite quando tiver array completo com 2 datas
        if (this.mode() === 'range') {
            if (Array.isArray(value) && value.length === 2) {
                this.setValue(value);
                this.onDateChange.emit(value);
            } else {
                // Range incompleto - não emite
                return;
            }
        } else {
            // Modo single - comportamento normal
            this.setValue(value);
            this.onDateChange.emit(value);
        }
    }

    onFocus(): void {
        this.markAsTouched();
    }

    onIconClick(): void {
        if (!this.isDisabled()) {
            this.datepicker.instance.open();
        }
    }

    // Métodos utilitários específicos do datepicker
    openCalendar(): void {
        this.datepicker?.instance?.open();
    }

    closeCalendar(): void {
        this.datepicker?.instance?.close();
    }

    clear(): void {
        this.datepicker?.instance?.clear();
        this.setValue('');
        this.onDateChange.emit('');
    }

    setToday(): void {
        const today = new Date();
        this.datepicker?.instance?.setDate(today);
        this.setValue(today);
        this.onDateChange.emit(today);
    }

    // Getter/setter para compatibilidade com template
    get currentValue(): any {
        return this.inputValue;
    }

    set currentValue(value: any) {
        this.inputValue = value;
        this.setValue(value);
    }

    // Método para acessibilidade
    getAriaDescribedBy(): string {
        const describedBy: string[] = [];

        if (this.hint()) {
            describedBy.push(this.inputId + '_hint');
        }

        return describedBy.join(' ') || '';
    }

    calendarButtonsPlugin = () => {
        return function (fp: any) {
            let buttonsAdded = false;

            const addButtons = () => {
                if (buttonsAdded || !fp.calendarContainer) return;

                const wrapper = document.createElement('div');
                wrapper.className = 'd-flex justify-content-between gap-2 my-2 px-2';

                // Botão "Limpar"
                const clearBtn = document.createElement('button');
                clearBtn.type = 'button';
                clearBtn.className = 'btn btn-outline-secondary flex-fill';
                clearBtn.textContent = 'Limpar';
                clearBtn.onclick = () => {
                    fp.clear();
                    fp.input.dispatchEvent(new Event('input'));
                    fp.close();
                };

                // Botão "Hoje"
                const todayBtn = document.createElement('button');
                todayBtn.type = 'button';
                todayBtn.className = 'btn btn-outline-primary flex-fill';
                todayBtn.textContent = 'Hoje';
                todayBtn.onclick = () => {
                    fp.setDate(new Date());
                    fp.input.dispatchEvent(new Event('input'));
                    fp.close();
                };

                wrapper.appendChild(clearBtn);
                wrapper.appendChild(todayBtn);

                // Inserir no calendário
                fp.calendarContainer.appendChild(wrapper);
                buttonsAdded = true;
            };

            return {
                onReady() {
                    // Tentar adicionar imediatamente
                    setTimeout(() => addButtons(), 0);
                },
                onOpen() {
                    // Garantir que os botões sejam adicionados quando o calendário abrir
                    addButtons();
                }
            };
        };
    };

    private getTimezoneOffset(): string {
        const offset = new Date().getTimezoneOffset();
        const hours = Math.floor(Math.abs(offset) / 60);
        const minutes = Math.abs(offset) % 60;
        const sign = offset <= 0 ? '+' : '-';
        return `${sign}${hours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}`;
    }
}
